import { startTransition } from "react"
import type { RefObject } from "react"

import {
    QueryClient,
    type QueryKey,
    type QueryState,
    type UseSuspenseQueryResult,
    useQueryClient,
    useSuspenseQuery
} from "@tanstack/react-query"
import type { AxiosError } from "axios"
import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"
import { useUpdateEffect } from "react-use"

import { minutes, seconds } from "@/3514/utils"

import {
    ECoachDashboardQueryCacheTime as ECacheTime,
    ECoachDashboardQueryKey as EQueryKey
} from "./coach.dashboard.api.config"
import adapters from "./coach.dashboard.api.queries.adapters"
import fetchers from "./coach.dashboard.api.queries.fetchers"
import type {
    ICoachDashboardParticipantsQueryDto as IParticipantsQueryDto,
    ICoachDashboardParticipantsQueryModel as IParticipantsQueryModel,
    ICoachSessionWithoutAttendanceMarksDto as ISessionWithoutAttendanceMarksDto,
    ICoachSessionWithoutAttendanceMarksModel as ISessionWithoutAttendanceMarksModel,
    ICoachDashboardSessionsQueryDto as ISessionsQueryDto,
    ICoachDashboardSessionsQueryModel as ISessionsQueryModel,
    ICoachDashboardParticipantsQueryParticipantDto as IUserParticipantDto,
    TCoachDashboardParticipantsQueryKeyPayload as TParticipantsKeyPayload,
    TCoachDashboardSessionsQueryKeyPayload as TSessionsKeyPayload
} from "./coach.dashboard.api.types"
import useCoachDashboardSelector from "./coach.dashboard.store.selector"
import { type ICoachDashboardState as IState } from "./coach.dashboard.store.slice"
import {
    ECoachDashboardActiveTab as EActiveTab,
    type TCoachDashboardTableSorter as TTableSorter,
    type TCoachDashboardTableSortersMap as TTableSortersMap
} from "./coach.dashboard.ui.types"
import uiUtils from "./coach.dashboard.ui.utils"

type TMarkedAttendancesQuery = UseSuspenseQueryResult<ISessionWithoutAttendanceMarksModel[], AxiosError>

type TSessionsQuery = UseSuspenseQueryResult<ISessionsQueryModel, AxiosError>
type TParticipantsQuery = UseSuspenseQueryResult<IParticipantsQueryModel, AxiosError>

function useSessionsQuery(key: TSessionsKeyPayload): ReturnType<(key: TSessionsKeyPayload) => TSessionsQuery> {
    return useSuspenseQuery<ISessionsQueryDto, AxiosError, ISessionsQueryModel>({
        queryKey: EQueryKey.Sessions(key),
        queryFn: fetchers.sessions,
        select: adapters.sessions,
        staleTime: minutes(5),
        gcTime: minutes(2),
        retry: 1,
        retryDelay: 4000,
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data", "isError", "isFetched"]
    })
}

function useParticipantsQuery(
    key: TParticipantsKeyPayload
): ReturnType<(key: TParticipantsKeyPayload) => TParticipantsQuery> {
    return useSuspenseQuery<IParticipantsQueryDto, AxiosError, IParticipantsQueryModel>({
        queryKey: EQueryKey.Participants(key),
        queryFn: fetchers.participants,
        select: adapters.participants,
        staleTime: minutes(5),
        gcTime: minutes(2),
        retry: 1,
        retryDelay: 4000,
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data", "isError", "isFetched"]
    })
}

function useDashboardQueries(): ReturnType<
    () => {
        [EActiveTab.Sessions]: (queryKey: TSessionsKeyPayload) => TSessionsQuery
        [EActiveTab.Participants]: (queryKey: TParticipantsKeyPayload) => TParticipantsQuery
    }
> {
    return { sessions: useSessionsQuery, participants: useParticipantsQuery }
}

function useAttendancesSuspenseQuery(): ReturnType<() => TMarkedAttendancesQuery> {
    return useSuspenseQuery<ISessionWithoutAttendanceMarksDto[], AxiosError, ISessionWithoutAttendanceMarksModel[]>({
        queryKey: EQueryKey.MarkedAttendances(),
        queryFn: fetchers.attendances,
        select: adapters.attendances,
        notifyOnChangeProps: ["data"],
        retry: 1,
        retryDelay: seconds(5),
        networkMode: "online",
        ...ECacheTime.MarkedAttendances()
    })
}

async function attendancesQueryPrefetcher({ qc }: { qc: QueryClient }): Promise<void> {
    await qc.prefetchQuery<
        ISessionWithoutAttendanceMarksDto[],
        AxiosError,
        ISessionWithoutAttendanceMarksModel[],
        QueryKey
    >({
        queryKey: EQueryKey.MarkedAttendances(),
        queryFn: fetchers.attendances,
        retry: 1,
        retryDelay: seconds(5),
        ...ECacheTime.MarkedAttendances()
    })
}

async function sessionsPrefetcher({ qc }: { qc: QueryClient }): Promise<void> {
    await qc.prefetchQuery<ISessionsQueryDto, AxiosError, ISessionsQueryModel, QueryKey>({
        queryKey: EQueryKey.Sessions({
            page: 1,
            period: "upcoming_sessions",
            order: "-start",
            search: ""
        }),
        queryFn: fetchers.sessions,
        staleTime: minutes(5),
        gcTime: minutes(2),
        retry: 1
    })
}

function useParticipantsSort(params: { sortersRef: RefObject<TTableSortersMap> }): void {
    const state: IState = useCoachDashboardSelector()

    const queryClient: QueryClient = useQueryClient()

    const queryKey: QueryKey = EQueryKey.Participants({
        search: state.searchValue,
        page: state.paginationPage
    })

    const queryState: QueryState = queryClient.getQueryState<IParticipantsQueryDto, AxiosError, QueryKey>(queryKey)

    useUpdateEffect((): void => {
        if (eq(state.activeTab, EActiveTab.Sessions)) return void 0

        if (isEmpty(queryState)) return void 0

        if (eq(queryState.status, "success"))
            startTransition(
                (): void => (
                    queryClient.setQueryData(queryKey, (prev: IParticipantsQueryDto): IParticipantsQueryDto => {
                        if (isEmpty(prev)) return prev

                        const sorter: TTableSorter =
                            params.sortersRef.current[state.sortConfig.colName][state.sortConfig.type]

                        const sortedResults: IUserParticipantDto[] = isEmpty(prev.results)
                            ? []
                            : sorter([...prev.results])

                        if (uiUtils.getIsTableSameSortOrder(prev.results, sortedResults)) return prev

                        return { ...prev, results: sortedResults }
                    }),
                    void 0
                )
            )

        return void 0
    }, [queryKey, state.activeTab, state.sortConfig.colName, state.sortConfig.type, queryState?.status])
}

export {
    useDashboardQueries as useCoachDashboardQueries,
    useAttendancesSuspenseQuery as useCoachDashboardAttendancesSuspenseQuery,
    attendancesQueryPrefetcher as coachDashboardAttendancesQueryPrefetcher,
    sessionsPrefetcher as coachDashboardSessionsPrefetcher,
    useParticipantsSort as useCoachDashboardParticipantsTableDataSort
}
export type { TSessionsQuery as TCoachDashboardSessionsQuery, TParticipantsQuery as TCoachDashboardParticipantsQuery }
